/**
 * MakeAnOfferTooltip Component
 */

import { getLicenseString, getLicenseUsagesFromQueryString } from '../../../../redux/helpers/license';
import {
  getBusinessEuropeanPhoneNumber,
  getBusinessMainPhoneNumber,
  getLicenseComponents,
} from '../../../../redux/selectors/staticData';
import { stopPropagation } from '../../../../utils/dom';
import './styles.scss';

import * as classNames from 'classnames';
import { template } from 'lodash';
import * as React from 'react';
import * as BEM from 'react-bem-helper';
import { connect } from 'react-redux';

import { CurrencyContext } from '../../../../contexts/currency';
import { QueryStringContext } from '../../../../contexts/queryString';
import Button from '../../../atoms/buttons/Button';
import { IconHelpFilled } from '../../../atoms/images/Icon';
import { Span } from '../../../atoms/text/Label';
import { Tooltip } from '../../../atoms/text/Tooltip';

interface IMakeAnOfferMailtoData {
  artist: string;
  currency: GQL.Currency;
  email: string;
  phone: string;
  terms: string;
  trackPrice: string;
  trackTitle: string;
}

export type MakeAndOfferContextProps = Optional<Pick<IMakeAnOfferMailtoData,
  | 'artist'
  | 'currency'
  | 'terms'
  | 'trackPrice'
  | 'trackTitle'
>>;

interface IMakeAnOfferTooltipOwnProps extends MakeAndOfferContextProps {
  buttonClassName?: string;
  className?: string;
  style?: React.CSSProperties;
  usages?: ILicenseUsage[];
  withButton?: boolean;
}

interface IMakeAnOfferTooltipStateProps {
  components: GQL.ILicenseComponents;
  email: string;
  phone: GQL.IBusinessPhoneNumber;
  euPhone: GQL.IBusinessPhoneNumber;
}

type MakeAnOfferTooltipProps = IMakeAnOfferTooltipStateProps & IMakeAnOfferTooltipOwnProps;

// tslint:disable:max-line-length no-invalid-template-strings
const emailSubject = 'Bopper - Make An Offer';
const emailBody = `
Hello Bopper!
%0D%0A
%0D%0AI’m interested in licensing the track '<%= trackTitle %>' by <%= artist %> and I would like to make you an offer.
%0D%0A
%0D%0AThe license fee quoted on Bopper for '<%= terms %>' is <%= trackPrice %> (<%= currency %>).
%0D%0A
%0D%0AHere is my proposed fee: (please enter your proposed fee)
%0D%0AHere is my phone number: (please enter your phone number)
%0D%0AAdditional info: (please enter any additional info)
%0D%0A
%0D%0APlease let me know if this is acceptable to you and your artist.
%0D%0A
%0D%0AThanks!
%0D%0A (Your name)
`;
const mailto = template(`mailto:<%= email %>?subject=${emailSubject}&body=${emailBody}`);
const message = template('If this price is outside of your budget, please get in touch. We would be happy to try and accommodate you. Call ${phone} or email ${email}.');
// tslint:enable:max-line-length no-invalid-template-strings

export const getMailtoLink = (data: IMakeAnOfferMailtoData) => mailto(data);
const classes = new BEM('make-an-offer-tooltip');

class MakeAnOfferTooltipBase extends React.PureComponent<MakeAnOfferTooltipProps> {
  renderWithButton() {
    return (
      <Button
        label="Make an offer"
        wrapperComponent="a"
        hyperLinkProps={{
          href: this.mailtoHref,
        }}
        colorScheme="light"
        size="xsmall"
        variant="pill"
        icon={this.renderTooltip()}
        iconPosition="right"
        withShadow
        {...classes('cta')}
      />
    );
  }

  renderTooltip() {
    const {
      className,
      email,
      phone: { phone_number: phone },
      euPhone: { phone_number: euPhone },
      style,
      withButton,
    } = this.props;

    const phoneStr = `${phone} (US) / ${euPhone} (EU)`;

    const extraClasses = classNames(className, withButton && 'button__icon');
    const tooltipContent = (
      <div onClick={stopPropagation}>
        <Span
          size="medium"
          block
          text={message({
            email: `<a class="button button--hyperlink" href="${this.mailtoHref}">${email}</a>`,
            phone: phoneStr,
          })}
        />
      </div>
    );

    return (
      <Tooltip content={tooltipContent} placement="bottom" theme="black" interactive {...classes('tooltip')}>
        <IconHelpFilled {...classes('', '', extraClasses)} style={style} />
      </Tooltip>
    );
  }

  render() {
    return this.props.withButton ? this.renderWithButton() : this.renderTooltip();
  }

  get mailtoHref() {
    const {
      artist = '',
      currency,
      email,
      phone: { phone_number: phone },
      terms = '',
      trackPrice = '',
      trackTitle = '',
    } = this.props;

    return getMailtoLink({ artist, currency: currency!, email, phone, terms, trackPrice, trackTitle });
  }
}

const MakeAnOfferTooltipWithCurrency = (props: MakeAnOfferTooltipProps) => {
  return (
    <CurrencyContext.Consumer>
      {({ currency }) => (
        <MakeAnOfferTooltipBase currency={currency} {...props} />
      )}
    </CurrencyContext.Consumer>
  );
};

const MakeAnOfferTooltipWithTerms = (props: Omit<MakeAnOfferTooltipProps, 'currency' | 'terms'>) => {
  return props.usages ? (
    <MakeAnOfferTooltipWithCurrency terms={getLicenseString(props.usages)} {...props} />
  ) : (
    <QueryStringContext.Consumer>
      {({ license_params }) => {
        const terms = getLicenseString(getLicenseUsagesFromQueryString({
          components: props.components,
          params: license_params || [],
        }));
        return (
          <MakeAnOfferTooltipWithCurrency terms={terms} {...props} />
        );
      }}
    </QueryStringContext.Consumer>
  );
};

export const MakeAnOfferTooltip =
  connect<IMakeAnOfferTooltipStateProps, {}, Omit<MakeAndOfferContextProps, 'currency' | 'terms'>, IStoreState>(
  (state) => ({
    email: 'MakeAnOffer@boppermusic.com',
    phone: getBusinessMainPhoneNumber(state),
    euPhone: getBusinessEuropeanPhoneNumber(state),
    components: getLicenseComponents(state),
  }),
)(MakeAnOfferTooltipWithTerms);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/components/molecules/messaging/MakeAnOfferTooltip/index.tsx